class EventTracker {
  constructor(options = {}) {
    this.config = Object.assign({ debug: false }, options);
    this.clickEvents = {
      cta: 'CTA Click',
      phone: 'Phone Click',
    };
    this.handleClick = this.handleClick.bind(this);
    this.track = this.track.bind(this);
  }

  init() {
    if (window.__samndanEventTrackingInitialized) {
      return this;
    }

    // No submit listener here: form attempt/success/error lifecycle is emitted
    // by svelte-forms sendData(), which sees the full merged payload for
    // multi-step forms.
    document.addEventListener('click', this.handleClick);

    window.siteTracking = window.siteTracking || {};
    window.siteTracking.trackEvent = this.track;
    window.__samndanEventTrackingInitialized = true;

    if (this.config.debug) {
      console.log('Event Tracker Loaded!');
    }

    return this;
  }

  cleanUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      return url.protocol === 'http:' || url.protocol === 'https:'
        ? url.origin + url.pathname
        : url.protocol + url.pathname;
    } catch (error) {
      return '';
    }
  }

  // Canonical page properties are applied last so nothing carried in with an
  // event -- a hidden form field, a data-event-* attribute -- can shadow the
  // sanitized values. A colliding incoming value is kept under a field_ prefix
  // rather than dropped, so submitted data is still visible in the event.
  withCanonicalProperties(properties) {
    const merged = Object.assign({}, properties || {});
    const canonical = {
      page_url: this.cleanUrl(window.location.href),
      page_path: window.location.pathname,
    };

    Object.keys(canonical).forEach(function (key) {
      if (merged[key] !== undefined && merged[key] !== canonical[key]) {
        merged['field_' + key] = merged[key];
      }
    });

    return Object.assign(merged, canonical);
  }

  // Events go to Heirial, which forwards them to analytics. The stub Heirial
  // puts in the head queues calls until its SDK loads; on a page without it
  // (Heirial not serving the site) the event is dropped, never thrown.
  track(eventName, properties) {
    try {
      if (window.heirial && typeof window.heirial.track === 'function') {
        window.heirial.track(eventName, this.withCanonicalProperties(properties));
      }
    } catch (error) {
      // Tracking cannot interrupt navigation or form behavior.
    }
  }

  getProperties(element, control) {
    const clickedControl = control || element;
    // page_url/page_path are not seeded here: track() applies them last.
    const properties = {
      tracker: element.getAttribute('data-event-tracker'),
    };

    Array.from(element.attributes).forEach(function (attribute) {
      if (attribute.name.indexOf('data-event-') !== 0 || attribute.name === 'data-event-tracker') {
        return;
      }

      properties[attribute.name.slice(11).replace(/-/g, '_')] = attribute.value;
    });

    if (!properties.location) {
      const locationElement = element.closest('[data-location]');
      if (locationElement) {
        properties.location = locationElement.getAttribute('data-location');
      }
    }

    const label = properties.tracker === 'form'
      ? ''
      : clickedControl.getAttribute('aria-label') || clickedControl.value || clickedControl.textContent;
    if (label) {
      properties.label = label.trim().replace(/\s+/g, ' ').slice(0, 200);
    }

    if (clickedControl.href) {
      properties.destination = this.cleanUrl(clickedControl.href);
    }

    return properties;
  }

  getTrackedElement(target) {
    if (!target || typeof target.closest !== 'function') {
      return null;
    }

    const phoneLink = target.closest('a[href^="tel:"]');
    if (phoneLink) {
      const trackedParent = phoneLink.closest('[data-event-tracker]');
      phoneLink.setAttribute('data-event-tracker', 'phone');
      phoneLink.setAttribute('data-event-type', phoneLink.getAttribute('data-event-type') || trackedParent?.getAttribute('data-event-type') || 'general');
      phoneLink.setAttribute('data-event-location', phoneLink.getAttribute('data-event-location') || trackedParent?.getAttribute('data-event-location') || 'content');
      return phoneLink;
    }

    return target.closest('[data-event-tracker]');
  }

  handleClick(event) {
    const element = this.getTrackedElement(event.target);
    if (!element) {
      return;
    }

    const tracker = element.getAttribute('data-event-tracker');
    const control = event.target.closest('a, button, input[type="button"], input[type="submit"]');

    if (tracker === 'cta' && (!control || !element.contains(control))) {
      return;
    }

    if (this.clickEvents[tracker]) {
      this.track(this.clickEvents[tracker], this.getProperties(element, control));
    }
  }
}

if (!window.__samndanEventTrackingInitialized) {
  window.EventTracker = EventTracker;
  window.eventTracker = new EventTracker().init();
}
